import classNames from 'classnames';
import { m as motion, useScroll } from 'framer-motion';
import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { Theme } from '@/shared/lib/types';
import EcosystemCard from './EcosystemCard';
import { DEFAULT_ECOSYSTEM_ITEMS, EcosystemItem } from './ecosystem-data';
import { ItemOffset, measureItemArcOffsets } from './ecosystem-orbit';
import { useEcosystemItemMotion } from './useEcosystemItemMotion';
import { useEcosystemScroll } from './useEcosystemScroll';

export type EcosystemSectionRawProps = {
    title: string;
    description?: string;
    items?: EcosystemItem[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    ecosystemSectionProps: EcosystemSectionRawProps;
    theme?: Theme;
};

function EcosystemSection({ ecosystemSectionProps, theme, className, ...props }: Props, ref: React.Ref<HTMLElement>) {
    const trackRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const orbitRef = useRef<HTMLDivElement>(null);
    const pictogramRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [itemOffsets, setItemOffsets] = useState<ItemOffset[]>([]);
    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';

    const items = useMemo(() => ecosystemSectionProps.items ?? DEFAULT_ECOSYSTEM_ITEMS, [ecosystemSectionProps.items]);

    const tpTitle = useTypograf(ecosystemSectionProps.title);
    const tpDescription = useTypograf(ecosystemSectionProps.description ?? '');

    const { scrollYProgress } = useScroll({
        target: trackRef,
        offset: ['start start', 'end end'],
    });

    const scrollMotion = useEcosystemScroll(scrollYProgress);
    const itemMotion = useEcosystemItemMotion(scrollYProgress, itemOffsets);

    useLayoutEffect(() => {
        const bottomEl = bottomRef.current;
        const orbitEl = orbitRef.current;

        if (!bottomEl || !orbitEl) {
            setItemOffsets([]);

            return;
        }

        const hasAllPictograms = items.every((_, index) => pictogramRefs.current[index]);

        if (!hasAllPictograms) {
            return;
        }

        const updateOffsets = () => {
            const nextOffsets = measureItemArcOffsets(bottomEl, orbitEl, pictogramRefs.current);

            setItemOffsets((prev) => {
                const isSame =
                    prev.length === nextOffsets.length &&
                    prev.every(
                        (offset, index) => offset.x === nextOffsets[index]?.x && offset.y === nextOffsets[index]?.y,
                    );

                return isSame ? prev : nextOffsets;
            });
        };

        updateOffsets();

        const observer = new ResizeObserver(() => {
            requestAnimationFrame(updateOffsets);
        });

        observer.observe(bottomEl);
        observer.observe(orbitEl);

        return () => observer.disconnect();
    }, [items]);

    const handlePictogramRef = (index: number) => (element: HTMLDivElement | null) => {
        pictogramRefs.current[index] = element;

        if (!element) {
            return;
        }

        const bottomEl = bottomRef.current;
        const orbitEl = orbitRef.current;
        const hasAllPictograms = items.every((_, itemIndex) => pictogramRefs.current[itemIndex]);

        if (!bottomEl || !orbitEl || !hasAllPictograms) {
            return;
        }

        requestAnimationFrame(() => {
            setItemOffsets(measureItemArcOffsets(bottomEl, orbitEl, pictogramRefs.current));
        });
    };

    const bottomBlock = (
        <div className="ecosystem-section__bottom" ref={bottomRef}>
            <motion.div
                className="ecosystem-section__orbit"
                ref={orbitRef}
                style={
                    isDesktop
                        ? {
                              opacity: scrollMotion.trackOpacity,
                              transformPerspective: 1000,
                              rotateX: scrollMotion.orbitRotateX,
                              scaleX: scrollMotion.orbitScaleX,
                              translateY: scrollMotion.orbitTranslateY,
                          }
                        : undefined
                }
            />
            <ul className="ecosystem-section__list list-unstyled">
                {items.map((item, index) => (
                    <EcosystemCard
                        key={index}
                        item={item}
                        itemMotion={isDesktop ? itemMotion[index] : undefined}
                        itemOffset={isDesktop ? undefined : itemOffsets[index]}
                        pictogramRef={handlePictogramRef(index)}
                    />
                ))}
            </ul>
        </div>
    );

    return (
        <section
            {...props}
            ref={ref}
            className={classNames('ecosystem-section', 'js-header-theme-trigger', className)}
            data-theme={theme}
        >
            <div
                className={classNames('ecosystem-section__track', !isDesktop && 'ecosystem-section__track--static')}
                ref={trackRef}
            >
                <div className="wrapper ecosystem-section__intro-wrapper">
                    <motion.div
                        className="ecosystem-section__intro"
                        style={isDesktop ? { scale: scrollMotion.titleScale } : undefined}
                    >
                        <h2 className="ecosystem-section__title h1">{tpTitle}</h2>
                        {tpDescription && (
                            <div
                                className="ecosystem-section__description text-l wysiwyg"
                                dangerouslySetInnerHTML={{ __html: tpDescription }}
                            />
                        )}
                    </motion.div>
                </div>
                <div className="ecosystem-section__sticky">
                    <div className="wrapper ecosystem-section__wrapper">{bottomBlock}</div>
                </div>
                <ul className="ecosystem-section__list-mobile list-unstyled">
                    {items.map((item, index) => (
                        <EcosystemCard key={index} item={item} />
                    ))}
                </ul>
            </div>
        </section>
    );
}

export default forwardRef(EcosystemSection);
